import { sql } from 'drizzle-orm'
import {
  check,
  date,
  index,
  pgEnum,
  pgTable,
  smallint,
  text,
  timestamp,
  uniqueIndex,
} from 'drizzle-orm/pg-core'

import { user } from './auth.ts'
import { cart } from './carts.ts'
import { createdAt, primaryId, updatedAt } from './columns.ts'
import { offeringVariant } from './offerings.ts'
import { order, orderItem } from './orders.ts'

/**
 * How many pieces of one level a performer will do on one day (spec §6:
 * "Miniaturkowiec raz w tygodniu wpisuje w kalendarzu, ile miniatur którego
 * poziomu zrobi danego dnia").
 *
 * This is a THIRD availability model and the schema now carries all three
 * deliberately, because they answer different questions:
 *
 *   `booking`                 — is this ROOM/CAMERA taken at this hour?
 *   `offering_availability`   — are we open at all at this hour? (I1 wires it)
 *   `capacity_day` (here)     — how many of these can the TEAM take that day?
 *
 * A piece of montage has no hour and no unit; it has a queue with a depth, and
 * the depth is per person and per level. Trying to express it as a booking
 * would mean inventing a fake time window for every thumbnail.
 *
 * The day is a `date`, not a timestamp: capacity is declared in Warsaw
 * wall-clock days by a person filling in a calendar, and storing an instant
 * would make "which day is this?" depend on the reader's zone.
 */
export const capacityDay = pgTable(
  'capacity_day',
  {
    id: primaryId('cap'),

    /** The performer whose day this is. `CASCADE` — capacity is not history. */
    userId: text('user_id')
      .notNull()
      .references(() => user.id, { onDelete: 'cascade' }),

    /** The level, e.g. Miniaturka · Creator. */
    variantId: text('variant_id')
      .notNull()
      .references(() => offeringVariant.id, { onDelete: 'cascade' }),

    day: date('day', { mode: 'string' }).notNull(),

    /** How many they are willing to take. Edited weekly by the performer. */
    capacity: smallint('capacity').notNull().default(0),

    /**
     * How many are SOLD — incremented inside the payment transaction (F5), and
     * the reason capacity is a row rather than a number in a settings blob.
     *
     * Kept beside `capacity` rather than counted from `order_item` on demand
     * for one reason: the `sold_count <= capacity` CHECK below can only exist
     * if both numbers live in the same row. A count derived at read time would
     * make over-selling representable and catchable only after the fact.
     */
    soldCount: smallint('sold_count').notNull().default(0),

    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => [
    // One row per person per level per day. Duplicates would multiply the
    // team's advertised capacity by however many rows had been collected.
    uniqueIndex('capacity_day_user_variant_day_unique').on(t.userId, t.variantId, t.day),
    // "How much of this level is free on this day?" — summed across every
    // performer, which is what the customer sees (anonymously, spec §6).
    index('capacity_day_variant_day_idx').on(t.variantId, t.day),

    check('capacity_day_capacity_nonneg', sql`${t.capacity} >= 0`),
    check('capacity_day_sold_nonneg', sql`${t.soldCount} >= 0`),
    // THE invariant. Spec §6: "Edycja w dół nie może zejść poniżej pojemności
    // zajętej." Enforced here rather than in the service because the service is
    // not the only writer — the payment transaction increments `sold_count`
    // too, and an over-sell is a slot two customers both paid for.
    check('capacity_day_sold_within_capacity', sql`${t.soldCount} <= ${t.capacity}`),
  ],
)

export type CapacityDay = typeof capacityDay.$inferSelect
export type NewCapacityDay = typeof capacityDay.$inferInsert

/**
 * Why a slot is being held, and therefore how long for.
 *
 * `CHECKOUT` is the mechanism spec §5 describes: ten minutes from entering
 * payment, so the last slot cannot be sold out from under somebody who is
 * mid-transaction.
 *
 * `AWAITING_PAYMENT` is the one reality forces. FLOU has no gateway yet (spec
 * §4), so "skuteczna płatność" is a human confirming a transfer, hours or days
 * later. Ten minutes would expire long before that and the slot would be resold
 * under an order the customer believes is placed. When the gateway lands this
 * mode simply stops being created — the table and the arithmetic do not change.
 */
export const slotHoldKind = pgEnum('slot_hold_kind', ['CHECKOUT', 'AWAITING_PAYMENT'])

/**
 * A soft claim on capacity, held for somebody who has not paid yet (spec §5).
 *
 * Soft is the operative word, and it is the spec's own design: "Slot pomniejsza
 * pojemność dopiero w momencie skutecznej płatności." A hold hides the day from
 * OTHER shoppers; it does not reserve anything in `capacity_day`. The hard
 * arbiter stays `claimSlotForVariant`, one atomic statement at payment, which is
 * what spec §5 point 4 means by "wygrywa pierwsza zakończona płatność" — the
 * holds narrow the odds of a collision, the claim decides it.
 *
 * Expiry is a PREDICATE, not a job: every read filters on `expires_at > now()`,
 * exactly as the abandoned-cart sweep does. A slot therefore returns to the pool
 * at the instant it expires whether or not anything has run, and the cleanup in
 * E5 only reclaims disk. A design that needed a worker to free capacity would
 * lose slots for as long as the worker was down.
 */
export const slotHold = pgTable(
  'slot_hold',
  {
    id: primaryId('hold'),

    kind: slotHoldKind('kind').notNull(),

    /**
     * The basket this hold was taken for. Survives the promotion to
     * `AWAITING_PAYMENT` rather than being cleared, so a hold can always be
     * traced back to where it came from.
     */
    cartId: text('cart_id').references(() => cart.id, { onDelete: 'cascade' }),

    /** Set when the basket became an order. NULL until then. */
    orderId: text('order_id').references(() => order.id, { onDelete: 'cascade' }),

    /** The level being held — availability is per level (spec §6). */
    variantId: text('variant_id')
      .notNull()
      .references(() => offeringVariant.id, { onDelete: 'cascade' }),

    day: date('day', { mode: 'string' }).notNull(),

    /** How many pieces. A basket of ten thumbnails on one day is ONE row of 10. */
    quantity: smallint('quantity').notNull().default(1),

    expiresAt: timestamp('expires_at', { withTimezone: true }).notNull(),

    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => [
    // One row per basket per level per day — the quantity is the count, not the
    // number of rows. Postgres treats NULLs as distinct, so rows already
    // promoted (and any future non-cart hold) never collide here.
    uniqueIndex('slot_hold_cart_variant_day_unique').on(t.cartId, t.variantId, t.day),
    // THE read: "how much of this level is held on these days, right now?"
    index('slot_hold_variant_day_idx').on(t.variantId, t.day, t.expiresAt),
    // The sweep in E5, and releasing a basket's holds when it converts or dies.
    index('slot_hold_expires_idx').on(t.expiresAt),
    index('slot_hold_order_idx').on(t.orderId),

    check('slot_hold_quantity_positive', sql`${t.quantity} > 0`),
    // Every hold has an owner appropriate to its kind. Without this a row could
    // lose both references — through a promotion that half-ran, say — and hold
    // capacity that nothing can ever release except the clock.
    check(
      'slot_hold_has_owner',
      sql`(${t.kind} = 'CHECKOUT' AND ${t.cartId} IS NOT NULL)
          OR (${t.kind} = 'AWAITING_PAYMENT' AND ${t.orderId} IS NOT NULL)`,
    ),
  ],
)

export type SlotHold = typeof slotHold.$inferSelect
export type NewSlotHold = typeof slotHold.$inferInsert

/**
 * Which days of somebody's capacity one order line is holding, and how much of
 * each (spec §8's long-form montage).
 *
 * ── WHY A LINE CAN NEED MORE THAN ONE DAY ────────────────────────────────
 * Every other kind of work this shop sells fits in a day: a thumbnail, a reel,
 * a shoot. Long-form montage does not. It is priced per MINUTE of finished film
 * (75/125/185 zł) in bands that run past eighty minutes, while a montażysta
 * declares the minutes they can cut in a day — so a forty-minute film against a
 * twenty-minute day is two days of one person's calendar, and the delivery date
 * is the second one.
 *
 * ── WHY A TABLE AND NOT A COLUMN ─────────────────────────────────────────
 * `order_item.delivery_day` holds ONE date, which was true while one line took
 * one slot, and `releaseSlot` is keyed on it. Widening that column to a span
 * would not be enough: a day may be taken only PARTLY (fifteen minutes of a
 * twenty-minute day, because five were already sold), so a refund that assumed
 * "give back everything on these dates" would hand back capacity the line never
 * held. The exact amount per day has to be written down.
 *
 * It also answers a question a performer genuinely asks and nothing could
 * answer before — "what am I actually doing on Tuesday" — by joining the other
 * way, which a JSON blob on the line could not.
 *
 * Rows are created inside the same transaction that spends the capacity, so a
 * claim and its record cannot disagree.
 */
export const orderItemCapacity = pgTable(
  'order_item_capacity',
  {
    id: primaryId('oicap'),

    /**
     * The line holding it. `CASCADE` — this is not a record of anything that
     * happened, it is a live claim on a calendar, and a claim belonging to a
     * deleted line is capacity nobody can give back.
     */
    orderItemId: text('order_item_id')
      .notNull()
      .references(() => orderItem.id, { onDelete: 'cascade' }),

    /** The declared day it came out of. `CASCADE` for the same reason. */
    capacityDayId: text('capacity_day_id')
      .notNull()
      .references(() => capacityDay.id, { onDelete: 'cascade' }),

    /**
     * How much of that day this line took, in the day's own unit — minutes for
     * montage, and the same weighted units everything else uses.
     *
     * Stored rather than derived: the alternative is dividing the line's length
     * by the day's capacity at refund time, which is wrong the moment a day was
     * only partly free when the sale landed.
     */
    units: smallint('units').notNull(),

    createdAt: createdAt(),
  },
  (t) => [
    // One row per line per day. Without it a retried claim could record the
    // same day twice and a refund would give back double.
    uniqueIndex('order_item_capacity_line_day_unique').on(t.orderItemId, t.capacityDayId),
    // "Give this line's capacity back" — the refund read.
    index('order_item_capacity_item_idx').on(t.orderItemId),
    // "What is on this day?" — the performer's own question, and the one no
    // shape before this could answer.
    index('order_item_capacity_day_idx').on(t.capacityDayId),
    // Zero is not a claim, it is a row somebody forgot to delete.
    check('order_item_capacity_units_positive', sql`${t.units} > 0`),
  ],
)

export type OrderItemCapacity = typeof orderItemCapacity.$inferSelect
export type NewOrderItemCapacity = typeof orderItemCapacity.$inferInsert
